// @ts-nocheck
import React, { useState, ChangeEvent, FormEvent } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { connect, ResolveThunks } from "react-redux";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import submitForm from "@store/Framework/Store/Store/Pages/Crm/Handlers/SubmitCrmForm";
import { FormFieldModel } from "@store/Framework/Types/ApiModels";
import { configurationFormProps } from "@store/Themes/common/props/configuratorForm";
import CustomerInformationFields, {
    CustomerFormErrors,
    CustomerFormState,
    emptyCustomerFormState,
    toCustomerInformationModel,
} from "@store/Library/Components/Forms/CustomerInformationFields";
import DatePicker, { DatePickerState } from "@insite/mobius/DatePicker";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";

const mapDispatchToProps = {
    submitForm,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ThemeProps<BaseTheme>;

interface ServiceRequestsFormStyles {
    block?: BlockProps;
    formContainer?: GridContainerProps;
    fieldItem?: GridItemProps;
    fullWidthItem?: GridItemProps;
    actionItem?: GridItemProps;
    commentField?: TextAreaPresentationProps;
    clearButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successInfosBox?: InfosBoxProps;
    successModal?: ModalPresentationProps;
    continueButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    formActions?: FormActionsProps;
}

export const serviceRequestsFormStyles: ServiceRequestsFormStyles = {
    block: configurationFormProps.block,
    formContainer: configurationFormProps.formContainer,
    fieldItem: configurationFormProps.fieldItem,
    fullWidthItem: configurationFormProps.fullWidthItem,
    actionItem: configurationFormProps.actionItem,
    clearButton: configurationFormProps.clearButton,
    submitButton: configurationFormProps.submitButton,
    successInfosBox: configurationFormProps.successInfosBox,
    successModal: configurationFormProps.successModal,
    continueButton: configurationFormProps.continueButton,
    loadingOverlay: configurationFormProps.loadingOverlay,
    formActions: configurationFormProps.formActions,
};

const Spacer = styled.div`
    width: 100%;
    margin-bottom: ${pxToRem(12)};
    border-bottom: 1px solid ${getColor("common.border")};
`;

type FormState = CustomerFormState & {
    orderNumber: string;
    serialNumber: string;
    partNumber: string;
    quantity: string;
    reportingLocation: string;
    dateOfFailureDiscovery: string;
    description: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
    ...emptyCustomerFormState(),
    orderNumber: "",
    serialNumber: "",
    partNumber: "",
    quantity: "",
    reportingLocation: "",
    dateOfFailureDiscovery: "",
    description: "",
};

const toFormFields = (form: FormState): FormFieldModel[] => {
    return [
        { name: "Order Number", value: form.orderNumber },
        { name: "Serial Number", value: form.serialNumber },
        { name: "Part Number", value: form.partNumber },
        { name: "Quantity", value: form.quantity },
        { name: "Reporting Location", value: form.reportingLocation },
        { name: "Date of failure discovery", value: form.dateOfFailureDiscovery },
        { name: "Description", value: form.description, format: "long" },
    ];
};

const styles = serviceRequestsFormStyles;

const overlayStyles = mergeProps(styles.loadingOverlay, {
    css: css`
        position: relative;
    `,
});

const ServiceRequestsForm = ({ submitForm }: Props) => {
    const [form, setForm] = useState<FormState>(initialState);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const [customerErrors, setCustomerErrors] = useState<CustomerFormErrors>({});
    const [reloadCustomerInformationFields, setReloadCustomerInformationFields] = useState(true);

    const [selectedDateOfFailure, setSelectedDateOfFailure] = useState<Date | undefined>(undefined);

    const maxDescriptionLength = 2000;

    const handleChange =
        (fieldName: keyof FormState) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const rawValue = event.target.value;
            const value =
                fieldName === "description" && rawValue.length > maxDescriptionLength
                    ? rawValue.slice(0, maxDescriptionLength)
                    : rawValue;

            setForm(prev => ({ ...prev, [fieldName]: value }));

            if (errors[fieldName]) {
                setErrors(prev => ({ ...prev, [fieldName]: undefined }));
            }

            setReloadCustomerInformationFields(false);
        };

    const validate = (): boolean => {
        const newErrors: FormErrors = { ...customerErrors };

        if (!form.description) {
            newErrors.description = translate("ServiceRequestForm.Description.Required") as string;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setForm(initialState);
        setErrors({});
        setSubmitError(undefined);
        setIsSuccessModalOpen(false);
        setReloadCustomerInformationFields(true);
        setSelectedDateOfFailure(undefined);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        setSubmitError(undefined);

        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        submitForm({
            formType: "ServiceRequest",
            formFields: toFormFields(form),
            customerInformation: toCustomerInformationModel(form),
            onSuccess: onSubmitFormSuccess,
            onError: onSubmitFormError,
            onComplete: onSubmitFormComplete,
        });
    };

    const onSubmitFormSuccess = () => {
        setIsSuccessModalOpen(true);
    };

    const onSubmitFormComplete = () => {
        setIsSubmitting(false);
    };

    const onSubmitFormError = (error: string) => {
        setSubmitError(error);
    };

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
        handleClear();
    };

    const dateOfFailureChange = ({ selectedDay }: DatePickerState) => {
        if (selectedDay) {
            const formattedDate = getLocalizedDateTime({
                dateTime: selectedDay,
            });

            setForm(prev => ({ ...prev, dateOfFailureDiscovery: formattedDate }));

            setSelectedDateOfFailure(selectedDay);
        }
    };

    return (
        <Block {...styles.block}>
            <FormWrapper generalErrorMessage={submitError}>
                <LoadingOverlay {...overlayStyles} loading={isSubmitting}>
                    <GridContainer {...styles.formContainer} as="form" onSubmit={handleSubmit}>
                        <CustomerInformationFields
                            {...{
                                form,
                                updateCustomerState: customerState => {
                                    setForm(prev => ({ ...prev, ...customerState }));
                                },
                                onHandleChange: handleChange,
                                onValidationChange: (
                                    customerErrors: Partial<Record<keyof CustomerFormState, string>>,
                                ) => {
                                    setCustomerErrors(() => ({ ...customerErrors }));
                                },
                                errors,
                                reloadCustomerInformationFields,
                            }}
                        />
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                label={translate("ServiceRequestForm.OrderNumber.Label")}
                                value={form.orderNumber}
                                onChange={handleChange("orderNumber")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                label={translate("ServiceRequestForm.SerialNumber.Label")}
                                value={form.serialNumber}
                                onChange={handleChange("serialNumber")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                label={translate("ServiceRequestForm.PartNumber.Label")}
                                value={form.partNumber}
                                onChange={handleChange("partNumber")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                label={translate("ServiceRequestForm.Quantity.Label")}
                                value={form.quantity}
                                onChange={handleChange("quantity")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                label={translate("ServiceRequestForm.ReportingLocation.Label")}
                                value={form.reportingLocation}
                                onChange={handleChange("reportingLocation")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <DatePicker
                                label={translate("ServiceRequestForm.DateOfFailure.Label")}
                                data-test-selector="serviceRequest_dateOfFailure"
                                onDayChange={dateOfFailureChange}
                                selectedDay={selectedDateOfFailure}
                                dateTimePickerProps={{
                                    maxDate: new Date(),
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <TextArea
                                {...styles.commentField}
                                label={`${translate("ServiceRequestForm.Description.Label")}*`}
                                placeholder={translate("ServiceRequestForm.Description.Placeholder")}
                                value={form.description}
                                onChange={handleChange("description")}
                                rows={4}
                                error={errors.description}
                                hint={`${maxDescriptionLength - form.description.length} ${translate("characters left")}`}
                            />
                        </GridItem>
                        <GridItem {...styles.actionItem}>
                            <FormActions {...styles.formActions}>
                                <Button
                                    {...styles.clearButton}
                                    type="button"
                                    onClick={handleClear}
                                    data-test-selector="serviceRequest_clear"
                                >
                                    {translate("Clear")}
                                </Button>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || isSuccessModalOpen}
                                    data-test-selector="serviceRequest_requestQuote"
                                >
                                    {isSubmitting ? translate("Submitting...") : translate("Send")}
                                </Button>
                            </FormActions>
                        </GridItem>
                    </GridContainer>
                    <Modal
                        headline={translate("Form sent")}
                        contentLabel={translate("Your form has been submitted.")}
                        {...styles.successModal}
                        isOpen={isSuccessModalOpen}
                        handleClose={closeSuccessModal}
                    >
                        <InfosBox {...styles.successInfosBox}>
                            {translate("Your service request has been submitted.")}
                        </InfosBox>
                        <FormActions>
                            <Button
                                {...styles.continueButton}
                                onClick={closeSuccessModal}
                                data-test-selector="serviceRequest_continue"
                            >
                                {translate("Continue")}
                            </Button>
                        </FormActions>
                    </Modal>
                </LoadingOverlay>
            </FormWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(() => ({}), mapDispatchToProps)(withTheme(ServiceRequestsForm)),
    definition: {
        group: "My Account",
        icon: "form",
    },
};

export default widgetModule;
